import { Injectable } from '@angular/core';
import {
  loadStripe,
  Stripe,
  StripeAddressElement,
  StripeAddressElementOptions,
  StripeCardCvcElement,
  StripeCardExpiryElement,
  StripeCardNumberElement,
  StripeElements,
  StripeError,
  Token,
  PaymentMethod,
  Appearance
} from '@stripe/stripe-js';
import { StripeCardStyleConfig } from './models/stripe-config.interface';

@Injectable({
  providedIn: 'root'
})
export class StripeElementsService {
  private stripeInstance: Stripe | null = null;
  private elementsInstance: StripeElements | null = null;
  private cardNumberElementInstance: StripeCardNumberElement | null = null;
  private cardExpiryElementInstance: StripeCardExpiryElement | null = null;
  private cardCvcElementInstance: StripeCardCvcElement | null = null;
  private addressElementInstance: StripeAddressElement | null = null;

  private readonly defaultCardStyle: StripeCardStyleConfig = {
    base: {
      color: '#30313d',
      fontFamily: '-apple-system, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#757680'
      }
    },
    invalid: {
      color: '#df1b41',
      iconColor: '#df1b41'
    }
  };

  // Mirrors the appearance of the subscription payment form (client-management StripeService).
  private getDefaultAppearance(): Appearance {
    return {
      theme: 'stripe',
      variables: {
        borderRadius: '8px',
        colorPrimary: '#111111',
        colorText: '#30313d',
        colorDanger: '#df1b41',
        fontFamily: '-apple-system, "Segoe UI", Roboto, "Helvetica Neue", sans-serif'
      },
      rules: {
        '.Input': {
          backgroundColor: '#ffffff',
          border: '1px solid #e6e6e6',
          borderRadius: 'var(--borderRadius)',
          boxShadow: '0 1px 3px rgba(50, 50, 93, 0.10), 0 1px 1px rgba(0, 0, 0, 0.06)',
          padding: '12px 14px',
          transition: 'border-color 0.15s ease, box-shadow 0.15s ease'
        },
        '.Input:focus': {
          borderColor: '#111111',
          boxShadow: '0 1px 3px rgba(50, 50, 93, 0.10), 0 1px 1px rgba(0, 0, 0, 0.06)'
        },
        '.Input--invalid': {
          borderColor: '#df1b41'
        }
      }
    };
  }

  // Public getters for accessing elements
  get cardNumberElement(): StripeCardNumberElement | null {
    return this.cardNumberElementInstance;
  }

  get cardExpiryElement(): StripeCardExpiryElement | null {
    return this.cardExpiryElementInstance;
  }

  get cardCvcElement(): StripeCardCvcElement | null {
    return this.cardCvcElementInstance;
  }

  get addressElement(): StripeAddressElement | null {
    return this.addressElementInstance;
  }

  get isInitialized(): boolean {
    return this.stripeInstance !== null;
  }

  async initialize(publishableKey: string): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    if (!publishableKey) {
      throw new Error('Stripe publishable key is required');
    }

    this.stripeInstance = await loadStripe(publishableKey);

    if (!this.stripeInstance) {
      throw new Error('Failed to load Stripe SDK');
    }
  }

  createElements(): void {
    if (!this.stripeInstance) {
      throw new Error('Stripe must be initialized before creating elements');
    }

    this.elementsInstance = this.stripeInstance.elements({
      locale: 'en',
      appearance: this.getDefaultAppearance()
    });

    this.createCardElements();
    this.createAddressElement();
  }

  private createCardElements(): void {
    if (!this.elementsInstance) {
      throw new Error('Elements instance not available');
    }

    this.cardNumberElementInstance = this.elementsInstance.create('cardNumber', {
      style: this.defaultCardStyle,
      showIcon: true
    });

    this.cardExpiryElementInstance = this.elementsInstance.create('cardExpiry', {
      style: this.defaultCardStyle
    });

    this.cardCvcElementInstance = this.elementsInstance.create('cardCvc', {
      style: this.defaultCardStyle
    });
  }

  private createAddressElement(): void {
    if (!this.elementsInstance) {
      throw new Error('Elements instance not available');
    }

    const addressOptions: StripeAddressElementOptions = {
      mode: 'billing'
    };

    this.addressElementInstance = this.elementsInstance.create('address', addressOptions);
  }

  mountCardNumber(container: HTMLElement): void {
    this.mountElement(this.cardNumberElementInstance, container, 'Card number');
  }

  mountCardExpiry(container: HTMLElement): void {
    this.mountElement(this.cardExpiryElementInstance, container, 'Card expiry');
  }

  mountCardCvc(container: HTMLElement): void {
    this.mountElement(this.cardCvcElementInstance, container, 'Card CVC');
  }

  mountAddress(container: HTMLElement): void {
    this.mountElement(this.addressElementInstance, container, 'Address');
  }

  private mountElement(
    element: StripeCardNumberElement | StripeCardExpiryElement | StripeCardCvcElement | StripeAddressElement | null,
    container: HTMLElement,
    elementName: string
  ): void {
    if (!element) {
      throw new Error(`${elementName} element not created`);
    }

    if (!container) {
      throw new Error(`${elementName} container element not found`);
    }

    element.mount(container);
  }

  async createToken(): Promise<string> {
    if (!this.stripeInstance || !this.cardNumberElementInstance || !this.addressElementInstance) {
      throw new Error('Stripe elements not ready for token creation');
    }

    const addressValue = await this.addressElementInstance.getValue();

    if (!addressValue.complete) {
      throw new Error('Please complete the billing address');
    }

    const { token, error } = await this.stripeInstance.createToken(this.cardNumberElementInstance, {
      name: addressValue.value.name,
      address_line1: addressValue.value.address.line1,
      address_line2: addressValue.value.address.line2 ?? undefined,
      address_city: addressValue.value.address.city,
      address_state: addressValue.value.address.state,
      address_zip: addressValue.value.address.postal_code,
      address_country: addressValue.value.address.country
    });

    if (error) {
      throw this.handleStripeError(error);
    }

    if (!token?.id) {
      throw new Error('Failed to create payment token');
    }

    return token.id;
  }

  async createPaymentMethod(billingDetails?: any): Promise<PaymentMethod> {
    if (!this.stripeInstance || !this.cardNumberElementInstance) {
      throw new Error('Stripe elements not ready for payment method creation');
    }

    let finalBillingDetails = billingDetails;

    if (!billingDetails && this.addressElementInstance) {
      const addressValue = await this.addressElementInstance.getValue();

      if (!addressValue.complete) {
        throw new Error('Please complete the billing address');
      }

      finalBillingDetails = {
        name: addressValue.value.name,
        address: {
          line1: addressValue.value.address.line1,
          line2: addressValue.value.address.line2 ?? undefined,
          city: addressValue.value.address.city,
          state: addressValue.value.address.state,
          postal_code: addressValue.value.address.postal_code,
          country: addressValue.value.address.country
        }
      };
    }

    const { error, paymentMethod } = await this.stripeInstance.createPaymentMethod({
      type: 'card',
      card: this.cardNumberElementInstance,
      billing_details: finalBillingDetails
    });

    if (error) {
      throw this.handleStripeError(error);
    }

    if (!paymentMethod) {
      throw new Error('Failed to create payment method');
    }

    return paymentMethod;
  }

  private handleStripeError(error: StripeError): Error {
    const errorMessage = error.message || 'An error occurred while processing payment';
    return new Error(errorMessage);
  }

  destroy(): void {
    this.destroyElement(this.cardNumberElementInstance, 'cardNumber');
    this.destroyElement(this.cardExpiryElementInstance, 'cardExpiry');
    this.destroyElement(this.cardCvcElementInstance, 'cardCvc');
    this.destroyElement(this.addressElementInstance, 'address');

    this.cardNumberElementInstance = null;
    this.cardExpiryElementInstance = null;
    this.cardCvcElementInstance = null;
    this.addressElementInstance = null;
    this.elementsInstance = null;
  }

  private destroyElement(
    element: StripeCardNumberElement | StripeCardExpiryElement | StripeCardCvcElement | StripeAddressElement | null,
    elementName: string
  ): void {
    if (element) {
      try {
        element.destroy();
      } catch (error) {
        console.warn(`Failed to destroy ${elementName} element:`, error);
      }
    }
  }
}
